import React, { useState, useEffect, useRef } from 'react'
import { Spinner, Icon } from '@blueprintjs/core'
import { Card, Elevation } from '../components/ui/card'
import { IconNames } from '@blueprintjs/icons'
import { useChat } from '../hooks/useChat'
import { ChatMessage } from '../components/copilot/ChatMessage'
import { ChatInput } from '../components/copilot/ChatInput'
import { ConversationSidebar } from '../components/copilot/ConversationSidebar'

export function CopilotPage() {
    const {
        messages,
        isLoading,
        conversationId,
        lastCost,
        conversations,
        sendMessage,
        clearChat,
        createNewConversation,
        loadConversation,
        fetchConversations,
        updateConversation,
        deleteConversation,
    } = useChat()

    const [showSidebar, setShowSidebar] = useState(true)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    useEffect(() => {
        fetchConversations()
    }, [])

    const handleSendMessage = async (content: string) => {
        await sendMessage(content)
    }

    const handleNewConversation = async () => {
        await createNewConversation()
    }

    const handleConversationSelect = async (id: number) => {
        await loadConversation(id)
    }

    const handleConversationUpdate = async (
        id: number,
        update: { title?: string; is_archived?: boolean },
    ) => {
        await updateConversation(id, update)
    }

    const handleConversationDelete = async (id: number) => {
        await deleteConversation(id)
    }

    const handleSuggestionClick = (suggestion: string) => {
        handleSendMessage(suggestion)
    }

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Sidebar */}
            {showSidebar && (
                <ConversationSidebar
                    conversations={conversations}
                    currentConversationId={conversationId}
                    onNewConversation={handleNewConversation}
                    onConversationSelect={handleConversationSelect}
                    onConversationUpdate={handleConversationUpdate}
                    onConversationDelete={handleConversationDelete}
                    onRefreshConversations={fetchConversations}
                />
            )}

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setShowSidebar(!showSidebar)}
                            className="p-2 rounded-md hover:bg-gray-100"
                        >
                            <Icon icon={IconNames.MENU} />
                        </button>
                        <h1 className="text-lg font-medium">Recipe Copilot</h1>
                        {/* {conversationId && (
                            <span className="text-sm text-gray-500">
                                {conversationId}
                            </span>
                        )} */}
                    </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto bg-white">
                    {messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <Card elevation={Elevation.TWO} className="p-8 text-center">
                                <div className="mb-4">
                                    <Icon icon={IconNames.CHAT} iconSize={48} color="#137CBD" />
                                </div>
                                <h2 className="text-lg font-semibold mb-2">
                                    Welcome to Recipe Copilot
                                </h2>
                                <p className="text-gray-600 mb-4 max-w-md">
                                    I can help you create and optimize Amazon advertising recipes.
                                    Ask me about campaign management, keyword optimization, or any
                                    automation rules you'd like to set up.
                                </p>
                                <div className="text-sm text-left text-gray-500">
                                    <p>Try asking:</p>
                                    <ul className="mt-2 space-y-1 whitespace-nowrap">
                                        <li>
                                            • "How do I create a recipe to pause low-performing
                                            keywords?"
                                        </li>
                                        <li>• "Show me campaigns with high ACoS"</li>
                                        <li>• "Help me optimize my ad spend"</li>
                                    </ul>
                                </div>
                            </Card>
                        </div>
                    ) : (
                        <div className="p-4 space-y-4">
                            {messages.map((message, index) => (
                                <ChatMessage
                                    key={index}
                                    message={message}
                                    onSuggestionClick={handleSuggestionClick}
                                />
                            ))}
                            {isLoading && (
                                <div className="flex items-center justify-center p-4">
                                    <Spinner size={20} />
                                    <span className="ml-2 text-sm text-gray-500">
                                        Assistant is thinking...
                                    </span>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>

                {/* Input */}
                <div className="bg-white border-t border-gray-200">
                    <ChatInput onSendMessage={handleSendMessage} disabled={isLoading} />
                </div>
            </div>
        </div>
    )
}
